/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / erreurs axios comme U3106 */
/* eslint-disable react-hooks/exhaustive-deps -- effets alignés chargement id / handlers stables */
import React, { useEffect, useReducer, useState } from 'react';
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';

import U2000DefaultForm from '../../u2000Core/components/formControls/U2000DefaultForm';

import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000GetHandlers } from '../../u2000Core/hooks/handler/U2000UseGetHandlers';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import type { U2000SearchPayload, U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000SaveHandlers } from '../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from '../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3203ROLES } from '../U3203';
import U3203AiMissionHeader from './form/u3203AiMission/U3203AiMissionHeader';
import U3203AiMissionPromptsTab from './form/u3203AiMission/U3203AiMissionPromptsTab';
import U3203AiMissionParametersTab from './form/u3203AiMission/U3203AiMissionParametersTab';
import U3203AiMissionDefaultPromptsTab from './form/u3203AiMission/U3203AiMissionDefaultPromptsTab';
import { U3203AiMissionFormToolBar } from './form/u3203AiMission/U3203AiMissionFormToolBar';
import U3203AiMissionDeleteDlg from './common/U3203AiMissionDeleteDlg';
import { useU3203Context } from '../U3203Context';
import U2000AuditPanelComponent from '../../u2000Core/components/audit/U2000AuditPanelComponent';
import {
  buildCreateBody,
  buildDeleteBody,
  buildOrgConfigBody,
  buildUpdateBody,
  createNewU3203FormRow,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doReplaceOrgConfig,
  doSearch,
  doUpdateData,
  luHandleAppiError,
  normalizeU3203AiMissionFromApi,
  validateOrgConfigForm,
  type U3203AiMission,
  type U3203AiMissionFormRow,
  type U3203AiMissionSearchRow,
} from '../U3203Service';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U2000Tab from '../../u2000Core/components/tab/U2000Tab';
import {
  u2000CanEditSystemMissionOrgConfig,
  u2000CanManageScopeAwareEntity,
  u2000CanModifyU3203Mission,
  u2000SessionHasOrganization,
} from '../../u2000Core/tools/U2000SystemScopeAccess';

const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

export interface U3203AiMissionFormExtendedHandlers extends U2000FormStateHandlersReturn<U3203AiMissionFormRow> {
  addNewMission: () => void;
  modifyMission: () => void;
  deleteMission: () => Promise<void>;
}

const U3203AiMissionForm: React.FC = () => {
  const { id } = useParams();
  const { auth, hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3203Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith('fr');
  const listTitle = t('title', { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const canEditMissionHeader = (
    row: Pick<U3203AiMissionFormRow, 'systemScope'> | null | undefined,
    editMode: U2000FormStateEnumEditMode,
  ) =>
    u2000CanManageScopeAwareEntity({
      isSystemScope: row?.systemScope === true,
      hasRequiredRole: hasRole(editMode === U2000FormStateEnumEditMode.ADD ? U3203ROLES.ADD : U3203ROLES.EDIT),
      sessionOrganizationId: auth.user?.organizationId,
    });
  const canEditOrgConfig = (row: Pick<U3203AiMissionFormRow, 'systemScope'> | null | undefined) =>
    u2000CanEditSystemMissionOrgConfig({
      isSystemScope: row?.systemScope === true,
      hasEditRole: hasRole(U3203ROLES.EDIT),
      sessionOrganizationId: auth.user?.organizationId,
    });
  const canModifyMission = (row: Pick<U3203AiMissionFormRow, 'systemScope'> | null | undefined) =>
    u2000CanModifyU3203Mission({
      isSystemScope: row?.systemScope === true,
      hasEditRole: hasRole(U3203ROLES.EDIT),
      sessionOrganizationId: auth.user?.organizationId,
    });
  const canDeleteMission = (row: Pick<U3203AiMissionFormRow, 'systemScope'> | null | undefined) =>
    u2000CanManageScopeAwareEntity({
      isSystemScope: row?.systemScope === true,
      hasRequiredRole: hasRole(U3203ROLES.DELETE),
      sessionOrganizationId: auth.user?.organizationId,
    });

  const validateForm = (row: U3203AiMissionFormRow) => {
    const result: Record<string, string> = {};
    const isAdd = !row.id;

    if (isAdd) {
      const code = row.code?.trim().toUpperCase() ?? '';
      if (!code) {
        result['code'] = t('errCodeRequired', { ns: NS });
      } else if (code.length > 64) {
        result['code'] = t('errCodeMax', { ns: NS });
      } else if (!CODE_PATTERN.test(code)) {
        result['code'] = t('errCodePattern', { ns: NS });
      }
    }

    const title = row.title?.trim() ?? '';
    if (!title) {
      result['title'] = t('errTitleRequired', { ns: NS });
    } else if (title.length > 255) {
      result['title'] = t('errTitleMax', { ns: NS });
    }

    if (!row.systemPrompt?.trim()) {
      result['systemPrompt'] = t('errSystemPromptRequired', { ns: NS });
    } else if (row.systemPrompt.length > 100_000) {
      result['systemPrompt'] = t('errSystemPromptMax', { ns: NS });
    }

    const userTpl = row.userPromptTemplate ?? '';
    if (userTpl.length > 100_000) {
      result['userPromptTemplate'] = t('errUserPromptTemplateMax', { ns: NS });
    }

    const linkedLu = row.linkedLogicalUnit?.trim() ?? '';
    if (linkedLu.length > 16) {
      result['linkedLogicalUnit'] = t('errLinkedLogicalUnitMax', { ns: NS });
    }

    if (!row.systemScope && !row.defaultProviderId) {
      result['defaultProviderId'] = t('errDefaultProviderRequired', { ns: NS });
    }
    if (!row.systemScope && !row.defaultModelId) {
      result['defaultModelId'] = t('errDefaultModelRequired', { ns: NS });
    }

    if (typeof row.active !== 'boolean') {
      result['active'] = isFr ? 'Le statut actif est obligatoire.' : 'Active flag is required.';
    }

    return result;
  };

  const u3203BL: U2000FormSBusinessHandlers<U3203AiMissionFormRow> = {
    newRecord: () => createNewU3203FormRow(auth.user?.organizationId),

    validateForm,

    saveForme: async (row: U3203AiMissionFormRow, editMode: U2000FormStateEnumEditMode) => {
      const isOrgConfigSave = row.systemScope && !canEditMissionHeader(row, editMode) && canEditOrgConfig(row);
      if (row.systemScope && !canEditMissionHeader(row, editMode) && !canEditOrgConfig(row)) {
        return;
      }
      if (isOrgConfigSave) {
        const errors = validateOrgConfigForm(row, t, NS);
        if (Object.keys(errors).length > 0) {
          u3203FormExtended.setErrors(errors);
          return;
        }
        handleSave.saveData(row, editMode);
        return;
      }
      if (row.systemScope && !canEditMissionHeader(row, editMode)) {
        return;
      }
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3203FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },

    cancelForm: () => {
      const old = u3203FormExtended.getOldData() as U3203AiMissionFormRow | null;
      if (old) {
        u3203FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === '/U3203') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    onLeaveForm: () => {
      navigate('/U3203');
    },
  };

  const u3203FormHandlers = useU2000FormStateHandlers<U3203AiMissionFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3203BL,
  });

  const u3203FormExtended: U3203AiMissionFormExtendedHandlers = {
    ...u3203FormHandlers,
    addNewMission: () => {
      u3203FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyMission: () => {
      if (!canModifyMission(u3203FormExtended.getData())) return;
      u3203FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteMission: async () => {
      if (!canDeleteMission(u3203FormExtended.getData())) return;
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3203AiMissionSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3203AiMissionSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3203AiMissionFormRow>({
    doGetFn: (rowId: string) => {
      return doGetData(u2000AxiosAuthenticated, rowId);
    },
    onSuccess: (row: U3203AiMissionFormRow) => {
      const normalized = normalizeU3203AiMissionFromApi(row as unknown as U3203AiMission);
      gridStateHandlers.setDataById(normalized.id, normalized as unknown as U3203AiMissionSearchRow);
      u3203FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const crumbLabel = `${normalized.code}${normalized.title ? ` — ${normalized.title}` : ''}`;
      setLogicalUnitBreadcrumb([
        { href: '/U3203', label: listTitle },
        { label: crumbLabel },
      ]);

      if (normalized.systemScope && !canModifyMission(normalized)) {
        u3203FormExtended.setEditMode(U2000FormStateEnumEditMode.NONE);
      } else if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3203FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3203AiMissionFormRow>({
    doSaveFn: (row: U3203AiMissionFormRow, editMode: U2000FormStateEnumEditMode) => {
      const isOrgConfigSave = row.systemScope && !canEditMissionHeader(row, editMode) && canEditOrgConfig(row);
      if (isOrgConfigSave) {
        return doReplaceOrgConfig(u2000AxiosAuthenticated, row.id, buildOrgConfigBody(row));
      }
      if (row.systemScope && !canEditMissionHeader(row, editMode)) {
        return Promise.reject(new Error('System mission read-only'));
      }
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3203AiMissionFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeU3203AiMissionFromApi(row as unknown as U3203AiMission);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), normalized as unknown as U3203AiMissionSearchRow]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(row.id, normalized as unknown as U3203AiMissionSearchRow);
      }
      u3203FormExtended.afterSaveData(normalized);
      u3203FormExtended.setData({ ...normalized, revisionComment: '' });
      const orgConfigSaved =
        editMode === U2000FormStateEnumEditMode.EDIT &&
        normalized.systemScope &&
        u2000SessionHasOrganization(auth.user?.organizationId);
      toast.success(
        t(orgConfigSaved ? 'msgOrgConfigSaved' : 'msgLogicalUnitSaved', { ns: NS }),
        orgConfigSaved ? t('successTitle', { ns: NS }) : undefined,
      );

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === '/U3203') {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3203FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3203FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => {
      const data = u3203FormExtended.getData();
      return data ? [data as unknown as U3203AiMissionSearchRow] : [];
    },
    doDeleteFn: async (rowId) => {
      const data = u3203FormExtended.getData();
      if (!data) return;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(data));
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        toast.success(t('success0003', { key: (del as U3203AiMissionSearchRow).code, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate('/U3203', { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === 'new') {
      u3203FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3203FormExtended.setData(createNewU3203FormRow(auth.user?.organizationId));
      return;
    }
    // BL-0316: skip re-get when prev/next (or onSuccess navigate) already loaded this id.
    // A second get() would reset parameterDisplayValues to {} and flash-empty the Paramètres tab.
    const current = u3203FormExtended.getData();
    if (current?.id === id) return;
    handleGet.getData(id);
  }, [id]);

  if (u3203FormExtended.getData() === null) {
    return null;
  }

  const formTabs = [
    {
      id: 'tabPrompts',
      label: t('tabPrompts', { ns: NS }),
      content: (
        <U3203AiMissionPromptsTab
          formStateHandlers={u3203FormExtended as any}
          t={t}
          onMissionRestored={(row) => {
            gridStateHandlers.setDataById(row.id, row as unknown as U3203AiMissionSearchRow);
          }}
        />
      ),
    },
    {
      id: 'tabParameters',
      label: t('tabParameters', { ns: NS }),
      content: <U3203AiMissionParametersTab formStateHandlers={u3203FormExtended as any} t={t} />,
    },
    {
      id: 'tabDefaultPrompts',
      label: t('defaultPrompts.tab', { ns: NS }),
      content: <U3203AiMissionDefaultPromptsTab formStateHandlers={u3203FormExtended as any} t={t} />,
    },
    hasRole(U3203ROLES.AUDITS) && {
      id: 'tabAudits',
      label: t('tabAudits', { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={u3203FormExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U3203AiMissionAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3203AiMissionDeleteDlg handleDelete={handleDelete as any} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3203AiMissionFormToolBar formStateHandlers={u3203FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3203FormExtended as any}
      >
        <U3203AiMissionHeader formStateHandlers={u3203FormExtended as any} t={t} />

        <U2000Tab
          className="mt-4"
          tabs={formTabs}
          defaultTab="tabPrompts"
          activeTab={activeTab ?? 'tabPrompts'}
          setActiveTab={(tab) => setActiveTab(typeof tab === 'string' ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U3203AiMissionForm;
